import { useMemo } from "react";
import { Info } from "lucide-react";
import type { FactorEntry } from "../../../services/productFactorsService";
import type { StrategyPerfMetrics } from "../../../hooks/useStrategyData";
import { formatSignedPercentage } from "../../../utils/numberUtils";
import { collapseToDaily, fillDailyGaps } from "../../../hooks/useStrategyVsActualComparison";

interface ActualPerfData {
  factors: FactorEntry[];
  metrics: StrategyPerfMetrics | null;
  isLoading: boolean;
  error: string | null;
}

const BUCKET_MS = 24 * 60 * 60 * 1000;

export function buildSparklinePath(factors: FactorEntry[], width: number, height: number): string {
  if (factors.length < 2) return "";

  const sorted = [...factors].sort(
    (a, b) => new Date(a.t).getTime() - new Date(b.t).getTime()
  );

  const t0 = new Date(sorted[0].t).getTime();
  const bucketFactors = new Map<number, number>();
  for (const entry of sorted) {
    const ts = new Date(entry.t).getTime();
    const bucketIndex = Math.floor((ts - t0) / BUCKET_MS);
    const bucketTs = t0 + bucketIndex * BUCKET_MS;
    bucketFactors.set(bucketTs, (bucketFactors.get(bucketTs) ?? 1) * entry.f);
  }

  const buckets = [...bucketFactors.entries()].sort((a, b) => a[0] - b[0]);
  if (buckets.length < 1) return "";

  const tStart = t0 - BUCKET_MS;
  const tEnd = buckets[buckets.length - 1][0];
  const tRange = tEnd - tStart || 1;

  const padding = 2;
  const drawW = width - padding * 2;
  const drawH = height - padding * 2;

  let cum = 1;
  const points: Array<{ x: number; y: number }> = [{ x: padding, y: 0 }];
  for (const [ts, factor] of buckets) {
    cum *= factor;
    points.push({
      x: padding + ((ts - tStart) / tRange) * drawW,
      y: (cum - 1) * 100,
    });
  }

  if (points.length === 2) {
    points[1].x = padding + drawW;
  }

  const minY = Math.min(...points.map(p => p.y));
  const maxY = Math.max(...points.map(p => p.y));
  const rangeY = maxY - minY || 1;

  return points
    .map((p, i) => {
      const sx = Math.round(p.x * 10) / 10;
      const sy = Math.round((padding + drawH - ((p.y - minY) / rangeY) * drawH) * 10) / 10;
      return `${i === 0 ? "M" : "L"}${sx},${sy}`;
    })
    .join(" ");
}

interface RawPoint {
  ts: number;
  y: number;
}

// Cumulative return (%) points for one already-daily, gap-filled series, rebased
// to 0 at a shared commonStart (the leading 0-point sits one day before so
// commonStart carries its own return). Returns raw (un-normalized) points so
// multiple series can be plotted on a shared scale.
function buildComparisonSeriesPoints(
  dailyFromCommonStart: FactorEntry[],
  commonStartMs: number
): RawPoint[] {
  if (dailyFromCommonStart.length < 1) return [];

  const points: RawPoint[] = [{ ts: commonStartMs - BUCKET_MS, y: 0 }];
  let cum = 1;
  for (const entry of dailyFromCommonStart) {
    cum *= entry.f;
    points.push({ ts: new Date(entry.t).getTime(), y: (cum - 1) * 100 });
  }
  return points;
}

// Builds strategy + actual paths on a SHARED time axis and SHARED Y scale so the
// two lines are directly comparable. Inner-join on the start: when both draw,
// both rebase to 0% at the first UTC day they share (leading overhang of either
// series is clipped). Ends stay free -- each line runs to its own last day -- and
// each series is gap-filled (factor 1.00) so a missing interior day bridges flat
// instead of breaking the line.
export function buildComparisonPaths(
  strategyFactors: FactorEntry[],
  actualFactors: FactorEntry[] | undefined,
  width: number,
  height: number
): { strategyPath: string; actualPath: string } {
  // Collapse both series to one factor per UTC day before plotting, exactly as
  // the modal does (useStrategyVsActualComparison.collapseToDaily). Strategy
  // factors arrive at 30-minute slots while actual performance is daily; without
  // this they bucket on different grids and the overlay misaligns. The geometric
  // cumulative return is unchanged.
  const dailyStrategy = collapseToDaily(strategyFactors);
  const dailyActual = actualFactors ? collapseToDaily(actualFactors) : [];

  // Need at least two distinct UTC days to draw a line.
  if (dailyStrategy.length < 2) return { strategyPath: "", actualPath: "" };
  const actualDrawable = dailyActual.length >= 2;

  // Bridge interior calendar gaps with flat (1.00) days so the lines stay
  // contiguous when the two calendars differ (mirrors the modal).
  const filledStrategy = fillDailyGaps(dailyStrategy);
  const filledActual = actualDrawable ? fillDailyGaps(dailyActual) : [];

  // Inner-join start: when both lines draw, both begin at the later of the two
  // first days. When only strategy draws, it keeps its own start.
  const commonStartMs = actualDrawable
    ? Math.max(
        new Date(filledStrategy[0].t).getTime(),
        new Date(filledActual[0].t).getTime()
      )
    : new Date(filledStrategy[0].t).getTime();

  const fromCommon = (daily: FactorEntry[]) =>
    daily.filter((f) => new Date(f.t).getTime() >= commonStartMs);

  const clippedStrategy = fromCommon(filledStrategy);
  const clippedActual = actualDrawable ? fromCommon(filledActual) : [];

  if (clippedStrategy.length < 2) return { strategyPath: "", actualPath: "" };
  const actualPlottable = clippedActual.length >= 2;

  const sPts = buildComparisonSeriesPoints(clippedStrategy, commonStartMs);
  const aPts = actualPlottable
    ? buildComparisonSeriesPoints(clippedActual, commonStartMs)
    : [];

  const all = aPts.length ? [...sPts, ...aPts] : sPts;

  const padding = 2;
  const drawW = width - padding * 2;
  const drawH = height - padding * 2;

  const tStart = Math.min(...all.map(p => p.ts));
  const tEnd = Math.max(...all.map(p => p.ts));
  const tRange = tEnd - tStart || 1;

  const minY = Math.min(...all.map(p => p.y));
  const maxY = Math.max(...all.map(p => p.y));
  const rangeY = maxY - minY || 1;

  const toPath = (pts: RawPoint[]): string => {
    if (pts.length < 2) return "";
    return pts
      .map((p, i) => {
        const sx = Math.round((padding + ((p.ts - tStart) / tRange) * drawW) * 10) / 10;
        const sy = Math.round((padding + drawH - ((p.y - minY) / rangeY) * drawH) * 10) / 10;
        return `${i === 0 ? "M" : "L"}${sx},${sy}`;
      })
      .join(" ");
  };

  return {
    strategyPath: toPath(sPts),
    actualPath: aPts.length ? toPath(aPts) : "",
  };
}

function ComparisonSparkline({
  strategyPath,
  actualPath,
  width,
  height,
  displayHeight = 260,
  appearance = "default",
}: {
  strategyPath: string;
  actualPath?: string;
  width: number;
  height: number;
  displayHeight?: number;
  appearance?: "default" | "alpha";
}) {
  if (!strategyPath) return <div style={{ height: displayHeight }} />;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full"
      style={{ height: displayHeight, display: "block" }}
    >
      {[0.25, 0.5, 0.75].map((frac) => (
        <line
          key={`h-${frac}`}
          x1={0} y1={height * frac}
          x2={width} y2={height * frac}
          className={appearance === "alpha" ? "stroke-v7-ink/10" : "stroke-gray-800"} strokeWidth={0.5}
        />
      ))}
      {Array.from({ length: 5 }).map((_, i) => (
        <line
          key={`v-${i}`}
          x1={width * ((i + 1) / 6)} y1={0}
          x2={width * ((i + 1) / 6)} y2={height}
          className={appearance === "alpha" ? "stroke-v7-ink/10" : "stroke-gray-800"} strokeWidth={0.5}
        />
      ))}
      {/* Actual drawn first so the Strategy line sits on top where they overlap */}
      {actualPath && (
        <path
          d={actualPath}
          fill="none"
          className={appearance === "alpha" ? "stroke-v7-bronze-dark" : "stroke-rp-amber"}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      <path
        d={strategyPath}
        fill="none"
        className={appearance === "alpha" ? "stroke-v7-ink" : "stroke-gray-200"}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function LegendSwatch({ className }: { className: string }) {
  return <span className={`inline-block h-0.5 w-3 rounded-full ${className}`} />;
}

export default function StrategyPerformanceWidget({
  metrics,
  factors,
  isLoading,
  error,
  onClick,
  actualPerf,
  appearance = "default",
}: {
  metrics: StrategyPerfMetrics | null;
  factors: FactorEntry[];
  isLoading: boolean;
  error: string | null;
  onClick: () => void;
  actualPerf?: ActualPerfData;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  const rows: Array<[string, number | null]> = [
    ["Inception", metrics?.inception ?? null],
    ["Today", metrics?.returnToday ?? null],
    ["1M", metrics?.return1M ?? null],
    ["3M", metrics?.return3M ?? null],
  ];

  const CHART_W = 400;
  const CHART_H = 120;

  // Pass actual factors through unless errored; buildComparisonPaths collapses to
  // UTC daily and decides drawability (>= 2 distinct days), so the legend and the
  // line stay in sync off a single source of truth.
  const actualInput = actualPerf && !actualPerf.error ? actualPerf.factors : undefined;

  const { strategyPath, actualPath } = useMemo(
    () => buildComparisonPaths(factors, actualInput, CHART_W, CHART_H),
    [factors, actualInput]
  );
  const showActual = actualPath.length > 0;

  return (
    <div className={`overflow-hidden border ${isAlpha
      ? "rounded-v7-panel border-v7-ink/15 bg-v7-white"
      : "rounded-lg border-gray-800 bg-black"}`}>
      {isLoading ? (
        <div className="p-6 space-y-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between animate-pulse">
              <div className={`h-3 w-12 rounded ${isAlpha ? "bg-v7-mist" : "bg-gray-800"}`} />
              <div className={`h-3 w-16 rounded ${isAlpha ? "bg-v7-mist" : "bg-gray-800"}`} />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="p-6">
          <p className={`text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-500"}`}>Performance unavailable</p>
        </div>
      ) : !metrics ? (
        <div className="p-6">
          <p className={`text-xs ${isAlpha ? "text-v7-ink/50" : "text-gray-500"}`}>No performance data yet</p>
        </div>
      ) : (
        <div role="button" tabIndex={0} onClick={() => onClick()} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }} className="w-full text-left flex flex-col cursor-pointer">
          {/* Header: line-color legend + info */}
          <div className="flex items-center gap-3 px-4 pt-4 pb-2">
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-400"}`}>
                <LegendSwatch className={isAlpha ? "bg-v7-ink" : "bg-gray-200"} />
                Strategy
              </span>
              {showActual && (
                <span className={`inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider ${isAlpha ? "text-v7-ink/60" : "text-gray-400"}`}>
                  <LegendSwatch className={isAlpha ? "bg-v7-bronze-dark" : "bg-rp-amber"} />
                  Actual
                </span>
              )}
            </div>
            <div className="relative group ml-auto">
              <Info className={`h-3.5 w-3.5 cursor-help transition-colors ${isAlpha ? "text-v7-ink/60 hover:text-v7-ink" : "text-gray-400 hover:text-white"}`} />
              <div className={`pointer-events-none absolute right-0 top-full z-30 mt-1.5 w-72 rounded-lg border px-4 py-3 text-xs leading-relaxed opacity-0 shadow-lg transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 ${isAlpha
                ? "border-v7-white/20 bg-v7-ink text-v7-white/80"
                : "border-gray-700 bg-gray-900 text-gray-300"}`}>
                {showActual
                  ? "Strategy is the simulated performance of your strategy, excluding execution factors like fees, commissions, and latency. Actual is based on your real executed trades, including those factors."
                  : "This is the simulated performance of your strategy. This does not include any execution related factors like fees, commissions, latency, etc."}
              </div>
            </div>
          </div>

          {/* Chart area */}
          <div className="flex-1 px-2">
            <ComparisonSparkline
              strategyPath={strategyPath}
              actualPath={actualPath}
              width={CHART_W}
              height={CHART_H}
              appearance={appearance}
            />
          </div>

          {/* Metrics row */}
          <div className={`grid grid-cols-4 border-t ${isAlpha ? "border-v7-ink/15 bg-v7-paper" : "border-gray-800"}`}>
            {rows.map(([label, value]) => (
              <div key={label} className="py-3 px-2 text-center">
                <p className={`mb-0.5 text-[9px] ${isAlpha ? "text-v7-ink/60" : "text-gray-500"}`}>{label}</p>
                {value != null ? (
                  <p className={`font-mono text-sm font-bold ${value >= 0 ? isAlpha ? "text-v7-success" : "text-green-500" : isAlpha ? "text-v7-rust" : "text-red-500"}`}>
                    {formatSignedPercentage(value, 2)}
                  </p>
                ) : (
                  <p className={`font-mono text-[11px] ${isAlpha ? "text-v7-ink/40" : "text-gray-600"}`}>--</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
